import type { InvoiceBase, InvoiceLine } from '@portal/types/nexudus';
import type { DiscountLine } from '../BookingSummary';

/**
 * Per-type team discount percentages from the Nexudus teams API.
 * Each field maps to a Nexudus `Team.Discount*` property.
 */
export interface TeamDiscounts {
  /** DiscountExtraServices — booking discount percentage */
  bookings: number;
  /** DiscountTariffs — contract/tariff discount percentage */
  tariffs: number;
  /** DiscountTimePasses — time pass discount percentage */
  timePasses: number;
  /** DiscountCharges — charges discount percentage */
  charges: number;
}

export const NO_TEAM_DISCOUNTS: TeamDiscounts = {
  bookings: 0,
  tariffs: 0,
  timePasses: 0,
  charges: 0,
};

/**
 * A line is team-discount-eligible if it's a booking or tariff line.
 *
 * Bookings receive team discount via `DiscountExtraServices`, tariffs via
 * `DiscountTariffs`. Event tickets and products do not.
 *
 * - Booking line: `BookingUniqueId` set, `TariffName` null
 * - Tariff line:  `TariffName` set, `BookingUniqueId` null
 * - Both exclude: `CoworkerProductName` set (products / contract benefits)
 */
export function isTeamDiscountEligible(line: InvoiceLine): boolean {
  if (line.CoworkerProductName != null) return false;
  return line.BookingUniqueId != null || line.TariffName != null;
}

/**
 * Return the applicable team discount percentage for a given invoice line.
 * Tariff lines use `tariffs`, booking lines use `bookings`, everything else 0.
 */
export function getLineTeamDiscountPct(
  line: InvoiceLine,
  discounts: TeamDiscounts,
): number {
  if (line.CoworkerProductName != null) return 0;
  if (line.TariffName != null) return discounts.tariffs;
  if (line.BookingUniqueId != null) return discounts.bookings;
  return 0;
}

/**
 * Derive the team discount amount from an invoice line when a promo code
 * is also active (stacked discounts).
 *
 * Nexudus bakes the team cut into the price with no trace, so it has to be
 * recovered from the price it was applied to: price × teamPct / (100 − teamPct).
 * Which price that is depends on the line type, because Nexudus stacks the two
 * discounts in opposite orders (pricing bible §4.1/§6):
 *
 * - Bookings: code off the base, team off the post-code price. `price` is the
 *   pre-credit net (`UnitPrice`, or `SubTotal + credit` on a credited line —
 *   credits reduce `UnitPrice` directly, staging test BP3).
 * - Tariffs: team off the base, code off the post-team price. `price` is the
 *   pre-credit net PLUS the line's `DiscountAmount`, which undoes the code and
 *   recovers the post-team price.
 */
export function deriveTeamDiscountAmount(
  price: number,
  teamPct: number,
): number {
  if (teamPct <= 0 || teamPct >= 100 || price <= 0) return 0;
  return (price * teamPct) / (100 - teamPct);
}

/**
 * Extract credit amount from an invoice line Description.
 * Nexudus embeds credit info as: `<small>Paid by booking credit (£X.XX)</small>`
 */
export function parseCreditFromDescription(description: string): number | null {
  const match = description?.match(/Paid by booking credit \(£([\d.]+)\)/);
  return match ? parseFloat(match[1]) : null;
}

/**
 * Decompose invoice line discounts into the summary's display lines, in the
 * order Nexudus applies them (BCD-1738 decision 1): discount code → team
 * discount → booking credits. The code row shows Nexudus's own DiscountAmount
 * (its face value — a fixed-£ code is indistinguishable from a percentage code
 * on the invoice line, so the face value is the only honest figure); the team
 * row is derived. Lines worth £0.00 are omitted entirely.
 *
 * Key Nexudus facts:
 * - When only team discount: DiscountAmount = team amount, DiscountCode = null
 * - When only Discount Code DiscountAmount = promo amount, DiscountCode = "<code>"
 * - When both: DiscountAmount = promo only, DiscountCode = "<code>", and the
 *   team cut is baked into the price — recovered by `deriveTeamDiscountAmount`
 */
export function getInvoiceDiscounts(
  invoice: InvoiceBase,
  teamDiscounts: TeamDiscounts,
): DiscountLine[] {
  let totalCredits = 0;
  /** Per-code promo totals — supports multiple promo codes across lines */
  const promoByCode = new Map<string, number>();
  /** Per-percentage team discount totals — handles mixed booking/tariff rates */
  const teamByPct = new Map<number, number>();

  const invoiceLines = invoice.Lines;
  if (!Array.isArray(invoiceLines)) return [];

  for (const line of invoiceLines) {
    const discount = line.DiscountAmount ?? 0;
    const linePct = getLineTeamDiscountPct(line, teamDiscounts);

    // Sum credits from any line (not just team-discount-eligible ones)
    const credit = parseCreditFromDescription(line.Description);
    if (credit != null && credit > 0) {
      totalCredits += credit;
    }

    // Promo discounts apply to any line; team discount only to eligible lines
    if (line.DiscountCode != null) {
      const code = line.DiscountCode;

      if (isTeamDiscountEligible(line) && linePct >= 100) {
        // A 100% team discount zeroes the line before the code can take
        // anything, and DiscountAmount holds the full base price (staging test
        // TD100 — team-alone; the team+code combination is still unverified,
        // bible gap 15). Attribute it to the team row and show no code row —
        // a 10% code must not be shown absorbing 100% of the price.
        teamByPct.set(linePct, (teamByPct.get(linePct) ?? 0) + discount);
      } else if (isTeamDiscountEligible(line) && linePct > 0) {
        // Code row at face value; team row derived from the price Nexudus
        // applied it to. Booking credits reduce UnitPrice directly (to 0 when
        // they cover the remainder — staging test BP3), so recover the
        // pre-credit price from SubTotal + credit. Tariff lines stack the
        // other way round (team first), so undo the code by adding
        // DiscountAmount back on before deriving.
        const preCreditPrice =
          credit != null && credit > 0
            ? (line.SubTotal ?? 0) + credit
            : line.UnitPrice;
        const basis =
          line.TariffName != null ? preCreditPrice + discount : preCreditPrice;
        teamByPct.set(
          linePct,
          (teamByPct.get(linePct) ?? 0) +
            deriveTeamDiscountAmount(basis, linePct),
        );
        promoByCode.set(code, (promoByCode.get(code) ?? 0) + discount);
      } else {
        promoByCode.set(code, (promoByCode.get(code) ?? 0) + discount);
      }
    } else if (isTeamDiscountEligible(line) && discount > 0) {
      // No promo — DiscountAmount is team discount
      teamByPct.set(linePct, (teamByPct.get(linePct) ?? 0) + discount);
    }
  }

  const lines: DiscountLine[] = [];

  // Display order (BCD-1738 decision 1): discount code, then team discount,
  // then booking credits — the order Nexudus applies them to bookings.
  // Lines worth nothing are never rendered — no "-£0.00" rows.
  for (const [code, amount] of promoByCode) {
    if (amount > 0.005) {
      lines.push({
        label: `Discount Code: ${code}`,
        amount: Math.round(amount * 100) / 100,
      });
    }
  }

  for (const [pct, amount] of teamByPct) {
    if (amount > 0.005) {
      lines.push({
        label: `Bruntwood Discount (${pct}%)`,
        amount: Math.round(amount * 100) / 100,
      });
    }
  }

  if (totalCredits > 0.005) {
    lines.push({
      label: 'Booking credits',
      amount: Math.round(totalCredits * 100) / 100,
      type: 'credit',
    });
  }

  return lines;
}
